import React, { useState, useEffect } from 'react';
import apiClient from '../../api/api';
import AuthLayout from '../../components/AuthLayout/AuthLayout';

const ReportSummerizeFile = () => {
  // State management
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [filters, setFilters] = useState({
    storageType: 'all',
    days: 'all',
    include: 'all'
    // Removed: useCache
  });
  
  // Pagination states
  const [problemFilesPage, setProblemFilesPage] = useState(1);
  const [missingFormatsPage, setMissingFormatsPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch data from API
  const fetchReportData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        include: filters.include,
        format: 'json'
        // Removed: use_cache parameter
      });
      
      // Only add storage_type if not 'all'
      if (filters.storageType !== 'all') {
        params.append('storage_type', filters.storageType);
      }
      
      // Only add days if not 'all'
      if (filters.days !== 'all') {
        params.append('days', filters.days);
      }

      const response = await apiClient.get(`/reports/files/summary?${params.toString()}`);
      setData(response.data);
      
    } catch (error) {
      console.error('Error fetching report data:', error);
      alert('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [filters]);

  // Helper functions
  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  const getHealthScoreColor = (score) => {
    if (score >= 90) return '#22c55e';
    if (score >= 70) return '#f59e0b';
    return '#ef4444';
  };

  const getProblemTypeLabel = (type) => {
    const labels = {
      'zero_size': 'Zero Size',
      'missing_path': 'Missing Path',
      'orphaned': 'Orphaned Files',
      'other': 'Other Issues'
    };
    return labels[type] || type;
  };

  // Calculate health score
  const calculateHealthScore = () => {
    if (!data.health_issues || !data.size_analytics) return 0;
    
    const totalFiles = data.size_analytics.overview?.[0]?.total_files || 1;
    const problemFiles = data.health_issues.problem_files?.reduce((sum, item) => sum + item.count, 0) || 0;
    
    return Math.max(0, Math.round(100 - (problemFiles / totalFiles * 100)));
  };

  // Export functionality
  const exportData = (format) => {
    alert(`Exporting data as ${format.toUpperCase()}...`);
    // TODO: Implement actual export functionality
  };

  if (loading) {
    return (
      <AuthLayout>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '200px',
          fontSize: '18px'
        }}>
          Loading...
        </div>
      </AuthLayout>
    );
  }

  const healthScore = calculateHealthScore();
  const sizeData = data.size_analytics?.overview?.[0] || {};
  const categoryData = data.size_analytics?.size_categories || [];
  const formatData = data.size_analytics?.format_distribution || [];
  const problemFiles = data.health_issues?.problem_files || [];
  const resourceData = data.resource_mapping?.resource_coverage?.[0] || {};

  return (
    <AuthLayout>
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', margin: '0 0 8px 0' }}>
            File Summary Report
          </h1>
          <p style={{ color: '#666', margin: '0 0 8px 0' }}>
            Comprehensive overview of file storage and health
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={fetchReportData}
            style={{
              padding: '8px 16px',
              border: '1px solid #3b82f6',
              backgroundColor: 'transparent',
              color: '#3b82f6',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            🔄 Refresh
          </button>
          <button
            onClick={() => exportData('csv')}
            style={{
              padding: '8px 16px',
              border: '1px solid #22c55e',
              backgroundColor: 'transparent',
              color: '#22c55e',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            📥 Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ 
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px',
        backgroundColor: '#f9fafb'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 16px 0' }}>Filters</h3>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
              Storage Type
            </label>
            <select
              value={filters.storageType}
              onChange={(e) => setFilters(prev => ({ ...prev, storageType: e.target.value }))}
              style={{
                width: '200px',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="all">All Storage</option>
              <option value="local">Local Storage</option>
              <option value="aws">AWS S3</option>
              <option value="b2">Backblaze B2</option>
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
              Time Range
            </label>
            <select
              value={filters.days}
              onChange={(e) => setFilters(prev => ({ ...prev, days: e.target.value === 'all' ? 'all' : parseInt(e.target.value) }))}
              style={{
                width: '200px',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="all">All Time</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards Row */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
        gap: '24px',
        marginBottom: '24px'
      }}>
        {/* Storage Overview Widget */}
        <div style={{ 
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '20px',
          backgroundColor: '#ffffff'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 16px 0' }}>
            Storage Overview
          </h3>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3b82f6' }}>
              {sizeData.total_size_gb || 0} GB
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>Total Storage Used</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
              <span>Total Files:</span>
              <span style={{ fontWeight: '600' }}>
                {(sizeData.total_files || 0).toLocaleString()}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
              <span>Average Size:</span>
              <span style={{ fontWeight: '600' }}>{sizeData.avg_size_mb || 0} MB</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
              <span>Largest File:</span>
              <span style={{ fontWeight: '600' }}>{sizeData.max_size_mb || 0} MB</span>
            </div>
          </div>
        </div>

        {/* File Health Status Widget */}
        <div style={{ 
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '20px',
          backgroundColor: '#ffffff'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 16px 0' }}>
            File Health Status
          </h3>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ 
              width: '100%', 
              height: '8px', 
              backgroundColor: '#f3f4f6', 
              borderRadius: '4px',
              marginBottom: '8px'
            }}>
              <div style={{ 
                width: `${healthScore}%`, 
                height: '100%', 
                backgroundColor: getHealthScoreColor(healthScore),
                borderRadius: '4px'
              }}></div>
            </div>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{healthScore}%</div>
            <div style={{ fontSize: '14px', color: '#666' }}>Health Score</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {problemFiles.map((problem, index) => (
              <div key={index} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>{getProblemTypeLabel(problem._id)}:</span>
                <span style={{ 
                  backgroundColor: '#fef2f2', 
                  color: '#dc2626', 
                  padding: '2px 8px', 
                  borderRadius: '12px',
                  fontSize: '12px'
                }}>
                  {problem.count}
                </span>
              </div>
            ))}
          </div>
          <button style={{
            width: '100%',
            padding: '8px 16px',
            marginTop: '16px',
            border: '1px solid #f59e0b',
            backgroundColor: '#fef3c7',
            color: '#d97706',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px'
          }}>
            ⚠️ View Issues
          </button>
        </div>

        {/* Resource Coverage Widget */}
        <div style={{ 
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '20px',
          backgroundColor: '#ffffff'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 16px 0' }}>
            Resource Coverage
          </h3>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#22c55e' }}>
              {Math.round((resourceData.multi_format_resources || 0) / (resourceData.total_resources || 1) * 100)}%
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>Multi-Format Coverage</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
              <span>Resources with SVG:</span>
              <span style={{ fontWeight: '600' }}>{resourceData.resources_with_svg || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
              <span>Resources with AI:</span>
              <span style={{ fontWeight: '600' }}>{resourceData.resources_with_ai || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
              <span>Resources with EPS:</span>
              <span style={{ fontWeight: '600' }}>{resourceData.resources_with_eps || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
              <span>Single Format Only:</span>
              <span style={{ 
                backgroundColor: '#fef3c7', 
                color: '#d97706', 
                padding: '2px 8px', 
                borderRadius: '12px',
                fontSize: '12px'
              }}>
                {resourceData.single_format_resources || 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Analytics Tables */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
        gap: '24px',
        marginBottom: '24px'
      }}>
        {/* Size Categories Table */}
        <div style={{ 
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          backgroundColor: '#ffffff'
        }}>
          <div style={{ padding: '20px 20px 0 20px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 16px 0' }}>
              Size Categories
            </h3>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                    Category
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                    Files
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                    Total Size
                  </th>
                </tr>
              </thead>
              <tbody>
                {categoryData.map((category, index) => (
                  <tr key={index}>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      <span style={{ 
                        backgroundColor: category._id === 'huge' ? '#fef2f2' : category._id === 'large' ? '#fef3c7' : '#f3f4f6',
                        color: category._id === 'huge' ? '#dc2626' : category._id === 'large' ? '#d97706' : '#374151',
                        padding: '4px 12px', 
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {category._id.charAt(0).toUpperCase() + category._id.slice(1)}
                      </span>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {category.count.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {category.total_size_mb} MB
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Format Distribution Table */}
        <div style={{ 
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          backgroundColor: '#ffffff'
        }}>
          <div style={{ padding: '20px 20px 0 20px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 16px 0' }}>
              Format Distribution
            </h3>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                    Format
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                    Files
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                    Total Size
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                    Avg Size
                  </th>
                </tr>
              </thead>
              <tbody>
                {formatData.slice(0, 10).map((format, index) => (
                  <tr key={index}>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      <span style={{ 
                        backgroundColor: '#eff6ff',
                        color: '#2563eb',
                        padding: '4px 12px', 
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {format._id?.toUpperCase() || 'Unknown'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {format.count.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {format.total_size_mb} MB
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {format.avg_size_mb} MB
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Problem Files Monitor */}
      <div style={{ 
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        marginBottom: '24px'
      }}>
        <div style={{ 
          padding: '20px', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #f3f4f6'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
            Problem Files Monitor
          </h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{
              padding: '6px 12px',
              border: '1px solid #f59e0b',
              backgroundColor: '#fef3c7',
              color: '#d97706',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}>
              Retry Migration
            </button>
            <button style={{
              padding: '6px 12px',
              border: '1px solid #dc2626',
              backgroundColor: '#fef2f2',
              color: '#dc2626',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}>
              Mark for Cleanup
            </button>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                  Issue Type
                </th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                  Count
                </th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                  Severity
                </th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {problemFiles.map((problem, index) => (
                <tr key={index}>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                    {getProblemTypeLabel(problem._id)}
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ 
                      backgroundColor: '#fef2f2', 
                      color: '#dc2626', 
                      padding: '4px 12px', 
                      borderRadius: '16px',
                      fontSize: '12px'
                    }}>
                      {problem.count}
                    </span>
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                    {problem._id === 'orphaned' ? (
                      <span style={{ 
                        backgroundColor: '#fef2f2',
                        color: '#dc2626',
                        padding: '4px 12px', 
                        borderRadius: '16px',
                        fontSize: '12px'
                      }}>
                        ❌ Critical
                      </span>
                    ) : (
                      <span style={{ 
                        backgroundColor: '#fef3c7',
                        color: '#d97706',
                        padding: '4px 12px', 
                        borderRadius: '16px',
                        fontSize: '12px'
                      }}>
                        ⚠️ Warning
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                    <button style={{
                      padding: '4px 12px',
                      border: 'none',
                      backgroundColor: 'transparent',
                      color: '#3b82f6',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}>
                      👁️ View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {problemFiles.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#6b7280' 
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
              <p>No critical issues found!</p>
            </div>
          )}
        </div>
      </div>

      {/* Resource Format Coverage */}
      <div style={{ 
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        backgroundColor: '#ffffff'
      }}>
        <div style={{ 
          padding: '20px', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #f3f4f6'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
            Resource Format Coverage
          </h3>
          <button style={{
            padding: '6px 12px',
            border: '1px solid #3b82f6',
            backgroundColor: '#eff6ff',
            color: '#2563eb',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
          }}>
            Generate Missing Formats
          </button>
        </div>
        
        <div style={{ padding: '20px' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ 
              textAlign: 'center', 
              padding: '16px', 
              backgroundColor: '#f9fafb', 
              borderRadius: '8px' 
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>
                {resourceData.total_resources || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Total Resources</div>
            </div>
            <div style={{ 
              textAlign: 'center', 
              padding: '16px', 
              backgroundColor: '#f9fafb', 
              borderRadius: '8px' 
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#22c55e' }}>
                {resourceData.multi_format_resources || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Multi-Format</div>
            </div>
            <div style={{ 
              textAlign: 'center', 
              padding: '16px', 
              backgroundColor: '#f9fafb', 
              borderRadius: '8px' 
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f59e0b' }}>
                {resourceData.single_format_resources || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Single Format</div>
            </div>
          </div>
          
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                    Format
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                    Available Resources
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                    Coverage %
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ 
                      backgroundColor: '#eff6ff',
                      color: '#2563eb',
                      padding: '4px 12px', 
                      borderRadius: '16px',
                      fontSize: '12px'
                    }}>
                      SVG
                    </span>
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                    {resourceData.resources_with_svg || 0}
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                    {Math.round((resourceData.resources_with_svg || 0) / (resourceData.total_resources || 1) * 100)}%
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ 
                      backgroundColor: '#f0fdf4',
                      color: '#15803d',
                      padding: '4px 12px', 
                      borderRadius: '16px',
                      fontSize: '12px'
                    }}>
                      ✅ Good
                    </span>
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ 
                      backgroundColor: '#eff6ff',
                      color: '#2563eb',
                      padding: '4px 12px', 
                      borderRadius: '16px',
                      fontSize: '12px'
                    }}>
                      AI
                    </span>
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                    {resourceData.resources_with_ai || 0}
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                    {Math.round((resourceData.resources_with_ai || 0) / (resourceData.total_resources || 1) * 100)}%
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ 
                      backgroundColor: '#fef3c7',
                      color: '#d97706',
                      padding: '4px 12px', 
                      borderRadius: '16px',
                      fontSize: '12px'
                    }}>
                      ⚠️ Needs Improvement
                    </span>
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ 
                      backgroundColor: '#eff6ff',
                      color: '#2563eb',
                      padding: '4px 12px', 
                      borderRadius: '16px',
                      fontSize: '12px'
                    }}>
                      EPS
                    </span>
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                    {resourceData.resources_with_eps || 0}
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                    {Math.round((resourceData.resources_with_eps || 0) / (resourceData.total_resources || 1) * 100)}%
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ 
                      backgroundColor: '#fef3c7',
                      color: '#d97706',
                      padding: '4px 12px', 
                      borderRadius: '16px',
                      fontSize: '12px'
                    }}>
                      ⚠️ Needs Improvement
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    </AuthLayout>
  );
};

export default ReportSummerizeFile;